import SiteMenu from "@/components/SiteMenu";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import CelestialBackground from "@/components/CelestialBackground";
import { BirthDatePicker, BirthTimePicker, SIGN_RANGES, signRangeLabel } from "@/components/BirthDateTime";
import AppleSignInButton from "@/components/AppleSignInButton";
import TrinityPreview from "@/components/TrinityPreview";
import { isNative } from "@/lib/native";

const HERO = "https://images.unsplash.com/photo-1507400492013-162706c8c05e?w=1600&auto=format&crop=entropy&cs=srgb&fm=jpg&q=70";

const inputCls =
  "w-full bg-[#0F1523] border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-base text-starlight placeholder:text-[#5f6b7d] focus:outline-none focus:border-[#D4AF37]/60 transition-colors";
const btnCls =
  "w-full flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium text-lg py-3.5 rounded-full transition-colors disabled:opacity-60";

const POINTS = [
  ["Your Sun, Moon and Rising", "Revealed in seconds from your birth date and time."],
  ["A fresh reading every morning", "Written from where the planets really are today."],
  ["Ask the stars anything", "Astra Interactive answers from your own chart."],
];

function DesktopAside() {
  return (
    <aside data-testid="join-desktop-aside" className="hidden lg:flex lg:sticky lg:top-0 lg:h-screen relative flex-col justify-center px-16 xl:px-24 border-r border-[#D4AF37]/15 overflow-hidden">
      <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#04060B]/70 via-[#04060B]/60 to-[#04060B]" />
      <div className="relative max-w-lg fade-up">
        <img src="/astra-icon-transparent.png" alt="" aria-hidden="true" className="w-24 h-24 mb-8 drop-shadow-[0_0_30px_rgba(212,175,55,0.35)]" />
        <p className="text-[11px] uppercase tracking-[0.35em] gold-text mb-3">It is written in the stars</p>
        <h2 className="text-5xl font-light leading-tight mb-10">Your horoscope, written from the real sky</h2>
        <ul className="space-y-6">
          {POINTS.map(([t, d]) => (
            <li key={t} className="flex gap-4">
              <span className="mt-2 w-2 h-2 rounded-full bg-[#D4AF37] shrink-0 shadow-[0_0_12px_#D4AF37]" />
              <div>
                <p className="text-xl text-starlight">{t}</p>
                <p className="text-base text-[#A7B0C0]">{d}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}

export function nextPath(params) {
  const n = params.get("next") || "";
  if (n === "ask") return `/ask${params.get("q") ? `?q=${encodeURIComponent(params.get("q"))}` : ""}`;
  return /^\/readings\/[a-z-]+$/.test(n) || /^\/pass\/[A-Za-z0-9_-]+$/.test(n) ? n : "/today";
}

export default function Onboarding() {
  const { login, register, setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = nextPath(params);
  const [mode, setMode] = useState(params.get("mode") === "login" ? "login" : "signup");
  const [step, setStep] = useState(1);
  const [guestSign] = useState(() => {
    const g = localStorage.getItem("astra_guest_sign");
    return g && SIGN_RANGES[g] ? g : null;
  });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(() => {
    const e = params.get("apple_error");
    if (!e) return "";
    return e === "cancelled" ? "Sign in with Apple was cancelled." : e === "no_email"
      ? "Apple didn't share an email address. Please try again and allow email sharing."
      : "Sign in with Apple didn't complete. Please try again.";
  });
  const [form, setForm] = useState({ name: "", birth_date: "", birth_time: "", birth_place: "", email: "", password: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const run = async (fn) => {
    setError("");
    setLoading(true);
    try {
      await fn();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  const reveal = (e) => {
    e.preventDefault();
    if (!form.birth_date) return setError("Please choose your date of birth");
    run(async () => {
      const { data } = await api.post("/public/preview", { birth_date: form.birth_date, birth_time: form.birth_time || null });
      setPreview(data);
      setStep(2);
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  const submit = (e) => {
    e.preventDefault();
    run(async () => {
      if (mode === "signup") {
        await register({ ...form, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
      } else {
        await login({ email: form.email, password: form.password });
      }
      navigate(next);
    });
  };

  const useDemo = () => run(async () => {
    const { data } = await api.post("/auth/demo");
    setUser(data);
    navigate("/today");
  });

  const switchMode = () => {
    setMode(mode === "signup" ? "login" : "signup");
    setStep(1);
    setError("");
  };

  const errorEl = error && <p className="text-sm text-red-300" data-testid="auth-error">{error}</p>;

  const wide = !isNative;

  return (
    <div className={`app-shell text-starlight relative ${wide ? "wide-desktop lg:grid lg:grid-cols-2" : ""}`}>
      <CelestialBackground />
      {wide && <DesktopAside />}
      <div className={`relative z-10 px-6 pb-16 ${wide ? "lg:flex lg:flex-col lg:items-center lg:justify-center lg:min-h-screen lg:py-10" : ""}`}>
        <div className={wide ? "lg:w-full lg:max-w-md lg:bg-[#0B0F19]/70 lg:backdrop-blur-xl lg:border lg:border-[#D4AF37]/20 lg:rounded-3xl lg:px-8 lg:pb-8" : ""}>
        <header className="safe-top box-content h-16 flex items-center gap-2">
          <SiteMenu />
          <Link to="/" data-testid="link-join-home" className="flex items-center gap-2">
            <ArrowLeft size={16} className="text-[#A7B0C0]" />
            <img src="/astra-mark-128.png" alt="" aria-hidden="true" className="w-9 h-9" />
            <span className="font-display text-lg tracking-[0.3em]">ASTRA</span>
          </Link>
        </header>

        <div className="fade-up">
          {mode === "signup" && step === 1 && (
            <>
              <p className="text-[11px] uppercase tracking-[0.3em] gold-text mb-1">Step 1 of 2</p>
              {guestSign && (
                <p data-testid="guest-sign-welcome" className="text-2xl font-light gold-text mb-1">Welcome, {guestSign}</p>
              )}
              <h1 className="text-3xl font-light mb-2">When were you born?</h1>
              <p className="text-sm text-[#A7B0C0] mb-5">We'll reveal your Sun, Moon and Rising signs and today's reading. No email needed yet.</p>
              <form data-testid="onboarding-form" onSubmit={reveal} className="space-y-3">
                <div>
                  <label className="text-xs text-[#A7B0C0] ml-1 block mb-1">Date of birth</label>
                  <BirthDatePicker testId="input-dob" sign={guestSign} value={form.birth_date} onChange={(v) => setForm({ ...form, birth_date: v })} />
                  {guestSign && (
                    <p data-testid="guest-sign-range" className="text-xs text-[#A7B0C0] ml-1 mt-1">
                      {guestSign} birthdays fall between {signRangeLabel(guestSign)}. Their months are listed first.
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-[#A7B0C0] ml-1 block mb-1">Time of birth (optional, for Rising sign)</label>
                  <BirthTimePicker testId="input-tob" value={form.birth_time} onChange={(v) => setForm({ ...form, birth_time: v })} />
                </div>
                <input data-testid="input-pob" className={inputCls} placeholder="City of birth (optional)" value={form.birth_place} onChange={set("birth_place")} />
                {errorEl}
                <button data-testid="btn-reveal-reading" type="submit" disabled={loading} className={btnCls}>
                  {loading ? <Loader2 className="animate-spin" size={20} /> : "Reveal my reading"}
                </button>
              </form>
            </>
          )}

          {mode === "signup" && step === 2 && preview && (
            <>
              <TrinityPreview preview={preview} onBack={() => setStep(1)} />
              <p className="text-[11px] uppercase tracking-[0.3em] gold-text mt-6 mb-1">Step 2 of 2</p>
              <h2 className="text-2xl font-light mb-2">Save your reading</h2>
              <p className="text-sm text-[#A7B0C0] mb-4">Create a free account to get a daily reading written for your full birth chart.</p>
              <form data-testid="signup-form" onSubmit={submit} className="space-y-3">
                <input data-testid="input-name" className={inputCls} placeholder="First name" value={form.name} onChange={set("name")} required />
                <input data-testid="input-email" type="email" className={inputCls} placeholder="Email" value={form.email} onChange={set("email")} required />
                <input data-testid="input-password" type="password" className={inputCls} placeholder="Password (6+ characters)" value={form.password} onChange={set("password")} required />
                {errorEl}
                <button data-testid="btn-submit-birth-details" type="submit" disabled={loading} className={btnCls}>
                  {loading ? <Loader2 className="animate-spin" size={20} /> : "Save & see my personal reading"}
                </button>
              </form>
            </>
          )}

          {mode === "login" && (
            <>
              <p className="text-[11px] uppercase tracking-[0.3em] gold-text mb-1">Welcome Back</p>
              <h1 className="text-3xl font-light mb-5">Return to your sanctuary</h1>
              <form data-testid="login-form" onSubmit={submit} className="space-y-3">
                <input data-testid="input-email" type="email" className={inputCls} placeholder="Email" value={form.email} onChange={set("email")} required />
                <input data-testid="input-password" type="password" className={inputCls} placeholder="Password" value={form.password} onChange={set("password")} required />
                {errorEl}
                <button data-testid="btn-submit-login" type="submit" disabled={loading} className={btnCls}>
                  {loading ? <Loader2 className="animate-spin" size={20} /> : "Enter"}
                </button>
              </form>
            </>
          )}

          <button
            data-testid="btn-demo-profile"
            onClick={useDemo}
            className="w-full mt-3 border border-[#D4AF37]/30 text-[#E8DCC4] text-base py-3 rounded-full hover:bg-[#D4AF37]/10 transition-colors"
          >
            Explore with a sample profile
          </button>
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-[#D4AF37]/15" />
            <span className="text-xs text-[#A7B0C0] uppercase tracking-widest">or</span>
            <div className="flex-1 h-px bg-[#D4AF37]/15" />
          </div>
          <AppleSignInButton next={next} />
          <p className="text-center text-base text-[#A7B0C0] mt-5">
            {mode === "signup" ? "Already aligned?" : "New to Astra?"}{" "}
            <button data-testid="toggle-auth-mode" onClick={switchMode} className="gold-text underline-offset-4 hover:underline">
              {mode === "signup" ? "Sign in" : "Create account"}
            </button>
          </p>
        </div>
        </div>
      </div>
    </div>
  );
}
